// ────────────────────────────────────────────────────────────────────────────
// Ferry port coordinates — one table, shared.
//
// There were two: an exact-name map in `orders/components/BookingFormFields`
// (for map pins on catalogue-created addresses with no geocoding) and a
// fragment-match list in `orders/lib/orderJourney` (for the journey panel's
// road legs). They overlapped on 18 ports and disagreed by up to 3 km, which is
// harmless for a map pin and not harmless for "how far is the delivery from the
// port".
//
// STOPGAP, deliberately visible. The route catalogue carries port NAMES and
// 3-letter codes but no coordinates; the right source is the carrier's terminal
// ADDRESS (the `IF-DUB` code convention in `orders/lib/route`), which is
// geocoded, org-specific and exact. Everything here prefers a real address when
// the caller has one — see `orderJourney.portCoordinates`.
// ────────────────────────────────────────────────────────────────────────────

export interface PortPoint {
  latitude: number;
  longitude: number;
}

interface PortEntry extends PortPoint {
  /** Canonical catalogue name, for an exact lookup. */
  name: string;
  /**
   * Lower-cased fragment for a loose match, so "Rosslare Harbour" and
   * "Rosslare Europort, Co. Wexford" both land on Rosslare.
   */
  match: string;
}

const PORTS: readonly PortEntry[] = [
  { name: 'Rosslare Europort', match: 'rosslare', latitude: 52.2508, longitude: -6.3383 },
  { name: 'Dublin Port', match: 'dublin', latitude: 53.3465, longitude: -6.2076 },
  { name: 'Ringaskiddy (Cork)', match: 'ringaskiddy', latitude: 51.8331, longitude: -8.3222 },
  { name: 'Cork', match: 'cork', latitude: 51.8404, longitude: -8.2942 },
  { name: 'Belfast', match: 'belfast', latitude: 54.6118, longitude: -5.9053 },
  { name: 'Larne', match: 'larne', latitude: 54.8503, longitude: -5.8047 },
  { name: 'Warrenpoint', match: 'warrenpoint', latitude: 54.0997, longitude: -6.2536 },
  { name: 'Fishguard Harbour', match: 'fishguard', latitude: 52.0117, longitude: -4.9836 },
  { name: 'Pembroke Dock', match: 'pembroke', latitude: 51.6947, longitude: -4.9469 },
  { name: 'Holyhead', match: 'holyhead', latitude: 53.3092, longitude: -4.6206 },
  { name: 'Birkenhead (Liverpool)', match: 'birkenhead', latitude: 53.3944, longitude: -3.0139 },
  { name: 'Liverpool', match: 'liverpool', latitude: 53.4489, longitude: -3.0086 },
  { name: 'Heysham', match: 'heysham', latitude: 54.0333, longitude: -2.9111 },
  { name: 'Cairnryan', match: 'cairnryan', latitude: 54.9756, longitude: -5.0203 },
  { name: 'Dover', match: 'dover', latitude: 51.1264, longitude: 1.3253 },
  { name: 'Calais', match: 'calais', latitude: 50.9686, longitude: 1.8536 },
  { name: 'Dunkirk', match: 'dunkirk', latitude: 51.0347, longitude: 2.3767 },
  { name: 'Cherbourg', match: 'cherbourg', latitude: 49.6486, longitude: -1.6222 },
  { name: 'Roscoff', match: 'roscoff', latitude: 48.7264, longitude: -3.9819 },
  { name: 'Zeebrugge', match: 'zeebrugge', latitude: 51.3325, longitude: 3.2078 },
  { name: 'Rotterdam', match: 'rotterdam', latitude: 51.9494, longitude: 4.1408 },
  { name: 'Rotterdam Europoort', match: 'europoort', latitude: 51.9536, longitude: 4.1356 },
  { name: 'Hook of Holland', match: 'hook of holland', latitude: 51.9789, longitude: 4.1283 },
  { name: 'Harwich', match: 'harwich', latitude: 51.9475, longitude: 1.2556 },
  { name: 'Hull', match: 'hull', latitude: 53.7397, longitude: -0.2756 },
  { name: 'Rotterdam (Vlaardingen)', match: 'vlaardingen', latitude: 51.8939, longitude: 4.3242 },
  { name: 'IJmuiden (Amsterdam)', match: 'ijmuiden', latitude: 52.4644, longitude: 4.5947 },
  { name: 'Cuxhaven', match: 'cuxhaven', latitude: 53.8722, longitude: 8.7047 },
  { name: 'Killingholme', match: 'killingholme', latitude: 53.6553, longitude: -0.2253 },
  { name: 'Immingham', match: 'immingham', latitude: 53.6297, longitude: -0.1872 },
  { name: 'Tilbury', match: 'tilbury', latitude: 51.4561, longitude: 0.3439 },
  { name: 'Purfleet', match: 'purfleet', latitude: 51.4783, longitude: 0.2347 },
  { name: 'Felixstowe', match: 'felixstowe', latitude: 51.9536, longitude: 1.3186 },
  { name: 'Teesport', match: 'teesport', latitude: 54.6097, longitude: -1.1597 },
  { name: 'Newcastle (North Shields)', match: 'north shields', latitude: 55.0047, longitude: -1.4497 },
  { name: 'Folkestone (Eurotunnel)', match: 'folkestone', latitude: 51.0961, longitude: 1.1286 },
  { name: 'Calais (Coquelles)', match: 'coquelles', latitude: 50.9256, longitude: 1.8019 },
  { name: 'Newhaven', match: 'newhaven', latitude: 50.7894, longitude: 0.0558 },
  { name: 'Dieppe', match: 'dieppe', latitude: 49.9289, longitude: 1.0858 },
  { name: 'Portsmouth', match: 'portsmouth', latitude: 50.8122, longitude: -1.0889 },
  { name: 'Caen (Ouistreham)', match: 'ouistreham', latitude: 49.2836, longitude: -0.2483 },
  { name: 'Le Havre', match: 'le havre', latitude: 49.4847, longitude: 0.1158 },
  { name: 'St Malo', match: 'st malo', latitude: 48.6422, longitude: -2.0167 },
  { name: 'Poole', match: 'poole', latitude: 50.7108, longitude: -1.9844 },
  { name: 'Plymouth', match: 'plymouth', latitude: 50.3633, longitude: -4.1592 },
  { name: 'Douglas', match: 'douglas', latitude: 54.1464, longitude: -4.4772 },
  { name: 'St Peter Port', match: 'st peter port', latitude: 49.4558, longitude: -2.5317 },
  { name: 'St Helier', match: 'st helier', latitude: 49.1797, longitude: -2.1103 },
  { name: 'Santander', match: 'santander', latitude: 43.4623, longitude: -3.8047 },
  { name: 'Bilbao', match: 'bilbao', latitude: 43.3547, longitude: -3.0219 },
];

/**
 * Coordinates for a port name, or null when it is not a port we know.
 *
 * <p>
 * Exact canonical name first, then the longest matching fragment. Longest
 * wins so "Ringaskiddy (Cork)" does not resolve to plain Cork, and
 * "Birkenhead (Liverpool)" does not resolve to Liverpool — both are real
 * terminals a few kilometres from the city they name.
 * </p>
 */
export function portCoordinatesByName(name: string | null | undefined): PortPoint | null {
  if (!name) return null;
  const needle = name.trim().toLowerCase();
  if (!needle) return null;

  const exact = PORTS.find((p) => p.name.toLowerCase() === needle);
  if (exact) return { latitude: exact.latitude, longitude: exact.longitude };

  const loose = PORTS.filter((p) => needle.includes(p.match)).sort(
    (a, b) => b.match.length - a.match.length,
  )[0];
  return loose ? { latitude: loose.latitude, longitude: loose.longitude } : null;
}

/** `{ lat, lng }`, for the map SDKs that want it that way. */
export function portLatLngByName(name: string | null | undefined): { lat: number; lng: number } | null {
  const point = portCoordinatesByName(name);
  return point ? { lat: point.latitude, lng: point.longitude } : null;
}
